import { Icon, Tooltip } from '@repo/ui';
import { GroupByOption } from '@repo/types';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';
import { numberUtil } from '@xtnd/web/shared/utils';
import { get, groupBy, keys, orderBy, sumBy } from 'lodash';
import { ArCollectionQueueFilter, ArCollectionQueueParams, Matter } from '../../types';
import { useTableLocalPagination, useTableParams } from '../../hooks';
import { useMatterARContext } from '../common';
import MatterActionToolbar from './MatterActionToolbar';
import { DAYS_PAST_DUE } from '../../constants';
import { useEffect, useMemo, useState } from 'react';
import { SortDirection } from '@xtnd/web/shared/components/DataTable';

type Props = {
  isLoading: boolean;
  matters: Matter[];
  onBackToClient: () => void;
};

interface ArMatterLocal {
  page: number;
  limit: number;
}

const LocalMatterTableInit: ArMatterLocal = {
  page: 1,
  limit: 25,
};

export default function MattersTable({ matters, isLoading, onBackToClient }: Props) {
  const { tableParams: queryParams } = useTableParams();
  const [matterGroupBy, setMatterGroupBy] = useState<GroupByOption>(GroupByOption.NONE);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [sorting, setSorting] = useState<Record<string, SortDirection>>({});
  const [tableLocalParams, setTableLocalParams] = useState(LocalMatterTableInit);

  const [localSearch, setLocalSearch] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (localSearch !== searchFilter) {
      setIsSearching(true);
    }
    const timeout = setTimeout(() => {
      setSearchFilter(localSearch);
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [localSearch, searchFilter]);

  const filters = queryParams.filters as ArCollectionQueueFilter;
  const groupByKey = useMemo(() => get(filters, 'groupBy'), [filters]);

  const { openMatterDrawer } = useMatterARContext();

  const getDisplayNameByGroupBy = () => {
    const rowData = matters && matters.length ? matters[0] : null;

    if (!rowData || !groupByKey) return '';

    switch (groupByKey) {
      case GroupByOption.CLIENT:
        return rowData.clientName;
      case GroupByOption.ORIGINATING:
        return rowData.originatingAttorneyName;
      case GroupByOption.RESPONSIBLE:
        return rowData.responsibleAttorneyName;
      default:
        return '';
    }
  };

  const isFilterByAtty = useMemo(
    () => groupByKey === GroupByOption.ORIGINATING || groupByKey === GroupByOption.RESPONSIBLE,
    [groupByKey]
  );

  const isFilterByClient = groupByKey === GroupByOption.CLIENT;

  const exportParams = useMemo(() => {
    const baseFilters = {
      ...(queryParams.filters as ArCollectionQueueFilter),
      matterGroupBy,
      ...(searchFilter && !selectedClient && { childSearch: searchFilter }),
    };

    if (selectedClient && selectedClientId) {
      return {
        ...queryParams,
        filters: {
          ...baseFilters,
          clientId: selectedClientId,
          matterGroupBy: GroupByOption.NONE,
          ...(searchFilter && { grandChildSearch: searchFilter }),
        },
      };
    }

    return {
      ...queryParams,
      filters: baseFilters,
    };
  }, [queryParams, matterGroupBy, selectedClient, selectedClientId, searchFilter]);

  const isMatterGroupByClient = matterGroupBy === GroupByOption.CLIENT && !selectedClient && isFilterByAtty;

  const mastersTableColumns: DataTableColumn<Matter>[] = useMemo(() => {
    if (isMatterGroupByClient) {
      return [
        {
          key: 'clientName',
          header: 'Client',
          align: 'left',
          isResizable: true,
          defaultWidth: 200,
          renderCell: row => (
            <Tooltip
              content={row.clientName ?? ''}
              position="auto"
              className="max-w-[18rem] rounded-md w-fit"
            >
              <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light max-w-[12rem]">
                <div className="text-sm overflow-hidden text-ellipsis">{row.clientName}</div>
              </div>
            </Tooltip>
          ),
        },
        {
          key: 'outstandingFees',
          header: 'Outstanding Fees',
          align: 'right',
          isResizable: true,
          defaultWidth: 150,
          isSortable: true,
          renderCell: row => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <div className="text-sm">{numberUtil.formatCurrency(row.outstandingFees || 0)}</div>
            </div>
          ),
        },
        {
          key: 'outstandingExpenses',
          header: 'Outstanding Expenses',
          align: 'right',
          isResizable: true,
          defaultWidth: 150,
          isSortable: true,
          renderCell: row => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <div className="text-sm">
                {numberUtil.formatCurrency(row.outstandingExpenses || 0)}
              </div>
            </div>
          ),
        },
        {
          key: 'totalOutstanding',
          header: 'Total AR',
          align: 'right',
          isResizable: true,
          defaultWidth: 150,
          isSortable: true,
          renderCell: row => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <div className="text-sm">{numberUtil.formatCurrency(row.totalOutstanding)}</div>
            </div>
          ),
        },
        {
          key: 'maxDaysPastDue',
          header: 'DPD',
          align: 'right',
          isResizable: true,
          defaultWidth: 150,
          isSortable: true,
          renderCell: row => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <div
                className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}
              >
                {row.maxDaysPastDue}
              </div>
            </div>
          ),
        },
        {
          key: 'numOfMatters',
          header: 'Matters',
          align: 'right',
          isResizable: true,
          defaultWidth: 150,
          renderCell: row => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <div className="text-sm">{row.numOfMatters}</div>
            </div>
          ),
        },
        {
          key: 'outstanding',
          header: 'O/S Invoices',
          align: 'right',
          isResizable: true,
          defaultWidth: 150,
          renderCell: row => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <div className="text-sm">{row.numOfInvoices}</div>
            </div>
          ),
        },
        {
          key: '',
          header: '',
          align: 'center',
          renderCell: () => (
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              <button>
                <Icon name="FaAngleRight" size="sm" className="opacity-100" />
              </button>
            </div>
          ),
        },
      ];
    }

    const columns: DataTableColumn<Matter>[] = [
      {
        key: 'clientName',
        header: 'Client',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <Tooltip
            content={row.clientName ?? ''}
            position="auto"
            className="max-w-[18rem] rounded-md w-fit"
          >
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light max-w-[12rem]">
              <div className="text-sm overflow-hidden text-ellipsis">{row.clientName}</div>
            </div>
          </Tooltip>
        ),
      },
      {
        key: 'matterDisplayNumber',
        header: 'Matter',
        align: 'left',
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <Tooltip
            content={row.matterDisplayNumber ?? ''}
            position="auto"
            className="max-w-[28rem] rounded-md w-fit"
          >
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light max-w-[12rem]">
              <div className="text-sm overflow-hidden text-ellipsis">{row.matterDisplayNumber}</div>
            </div>
          </Tooltip>
        ),
      },
      {
        key: 'responsibleAttorneyName',
        header: 'Responsible Attorney',
        align: 'left',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <Tooltip
            content={row.responsibleAttorneyName ?? ''}
            position="auto"
            className="max-w-[12rem] rounded-md w-fit"
          >
            <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light max-w-[8rem]">
              <div className="text-sm overflow-hidden text-ellipsis">
                {row.responsibleAttorneyName}
              </div>
            </div>
          </Tooltip>
        ),
      },
      {
        key: 'outstandingFees',
        header: 'Outstanding Fees',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm">{numberUtil.formatCurrency(row.outstandingFees || 0)}</div>
          </div>
        ),
      },
      {
        key: 'outstandingExpenses',
        header: 'Outstanding Expenses',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm">{numberUtil.formatCurrency(row.outstandingExpenses || 0)}</div>
          </div>
        ),
      },
      {
        key: 'totalOutstanding',
        header: 'Total AR',
        align: 'right',
        isResizable: true,
        defaultWidth: 150,
        isSortable: true,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm">{numberUtil.formatCurrency(row.totalOutstanding)}</div>
          </div>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'outstanding',
        header: 'O/S Invoices',
        align: 'right',
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="text-sm">{row.numOfInvoices}</div>
          </div>
        ),
      },
      {
        key: '',
        header: '',
        align: 'center',
        renderCell: () => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <button>
              <Icon name="FaAngleRight" size="sm" className="opacity-100" />
            </button>
          </div>
        ),
      },
    ];

    if (isFilterByClient) {
      return columns.filter(col => col.key !== 'clientName');
    }

    return columns;
  }, [isMatterGroupByClient, isFilterByClient]);

  const groupedMatters = useMemo(() => {
    let filteredMatters = matters;
    if (searchFilter) {
      const lower = searchFilter.toLowerCase();

      let searchFields: (keyof Matter)[];
      if (isFilterByClient || selectedClient) {
        searchFields = [
          'matterDisplayNumber',
          'responsibleAttorneyName',
          'originatingAttorneyName',
        ];
      } else if (isMatterGroupByClient) {
        searchFields = ['clientName'];
      } else {
        searchFields = ['matterDisplayNumber', 'clientName', 'responsibleAttorneyName'];
      }

      filteredMatters = matters.filter(matter =>
        searchFields.some(field => matter[field]?.toString().toLowerCase().includes(lower))
      );
    }

    if (isMatterGroupByClient) {
      const groupByClient = groupBy(filteredMatters, 'clientName');
      const groupedMatters = keys(groupByClient).map((key: string) => {
        const mats = groupByClient[key]! || [];

        return {
          clientId: mats[0]?.clientId,
          clientName: key,
          outstandingFees: sumBy(mats, 'outstandingFees'),
          outstandingExpenses: sumBy(mats, 'outstandingExpenses'),
          totalOutstanding: sumBy(mats, 'totalOutstanding'),
          maxDaysPastDue: Math.max(0, ...mats.map(matter => matter.maxDaysPastDue)),
          numOfMatters: mats.length,
          numOfInvoices: sumBy(mats, 'numOfInvoices'),
        } as Matter;
      });

      if (Object.keys(sorting).length > 0) {
        const [sortKey, order] = Object.entries(sorting)[0] || [];
        return orderBy(groupedMatters, sortKey, order);
      }

      return groupedMatters;
    }

    if (selectedClient) {
      const groupByClient = groupBy(filteredMatters, 'clientName');

      if (Object.keys(sorting).length > 0) {
        const [sortKey, order] = Object.entries(sorting)[0] || [];
        return orderBy(groupByClient[selectedClient], sortKey, order);
      }

      return groupByClient[selectedClient] || [];
    }

    if (Object.keys(sorting).length > 0) {
      const [sortKey, order] = Object.entries(sorting)[0] || [];
      return orderBy(filteredMatters, sortKey, order);
    }

    return filteredMatters;
  }, [matters, isMatterGroupByClient, selectedClient, sorting, searchFilter]);

  const paginated = useTableLocalPagination(
    groupedMatters,
    tableLocalParams.page,
    tableLocalParams.limit
  );

  const handleGetRowKey = (row: Matter, index: number) => {
    if (matterGroupBy === GroupByOption.CLIENT) {
      return `matter-groupBy-client-${row.clientName}-${index}`;
    }
    return `matter-row-${index}`;
  };

  const handleBackToClient = () => {
    setLocalSearch('');
    setSearchFilter('');

    if (selectedClient) {
      setSelectedClient('');
      setSelectedClientId('');
      return;
    }
    onBackToClient();
  };

  const handleOnClickRow = (row: Matter) => {
    if (!row.matterId) {
      setSelectedClient(row.clientName!);
      setSelectedClientId(row.clientId!);

      return;
    }

    return (
      row.matterId &&
      row.clientId &&
      openMatterDrawer(row.matterId, row.clientId, filters.daysPastDue)
    );
  };

  return (
    <DataTable
      stickyHeader
      columns={mastersTableColumns}
      data={{
        data: paginated.data,
        total: paginated?.pagination?.total ?? 0,
      }}
      pagination={paginated.pagination}
      onPaginationChange={pagination => {
        setTableLocalParams(prev => ({ ...prev, page: pagination.page, limit: pagination.limit }));
      }}
      onRowClick={handleOnClickRow}
      tableContainerClassName="overflow-y-auto relative"
      tableClassName="min-w-full divide-y divide-gray-200 relative"
      emptyState={{
        icon: 'HiClipboardList',
        title: 'No matters available',
        description: 'There are no matters found.',
      }}
      onSort={sortBy => setSorting({ ...sortBy })}
      sorting={sorting}
      isLoading={isLoading || isSearching}
      getRowKey={handleGetRowKey}
      renderHeader={() => (
        <MatterActionToolbar
          isFilterByAtty={isFilterByAtty}
          isFilterByClient={isFilterByClient}
          selectedClient={selectedClient}
          matterGroupBy={matterGroupBy}
          onMatterGroupByChange={setMatterGroupBy}
          groupBy={getDisplayNameByGroupBy() || ''}
          onBackToClients={handleBackToClient}
          search={localSearch}
          onSearchChange={setLocalSearch}
          queryParams={{ ...exportParams, limit: matters?.length }}
          isDataLoading={isLoading}
        />
      )}
      enableColumnResizing
      columnWidthsStorageKey={`matter-${matterGroupBy ? `groupBy-${matterGroupBy}-table` : 'table'}`}
    />
  );
}
